import { motion } from 'framer-motion';
import SEO from '../components/SEO';

const About = () => {
  const values = [
    {
      title: 'Authentic Vibes',
      description: 'Real connections with certified yoga schools that actually care',
      icon: '✨',
    },
    {
      title: 'Quality First',
      description: 'Only the best teacher training programs make it to our list',
      icon: '🔥',
    },
    {
      title: 'Your Journey',
      description: 'Personalized support because your path is uniquely yours',
      icon: '🌈',
    },
    {
      title: 'Good Energy',
      description: 'Building a community of passionate yoga teachers worldwide',
      icon: '💫',
    },
  ];

  const stats = [
    { number: '500+', label: 'Happy Students', emoji: '🧘‍♀️' },
    { number: '50+', label: 'Partner Schools', emoji: '🏫' },
    { number: '25+', label: 'Countries', emoji: '🌍' },
    { number: '100%', label: 'Dedication', emoji: '💯' },
  ];

  const reasons = [
    {
      title: 'We Get You',
      description: 'Started by yoga lovers who understand the journey',
      icon: '💜',
    },
    {
      title: 'Transparent AF',
      description: 'No hidden fees, no BS. Just real talk',
      icon: '👀',
    },
    {
      title: 'Always Here',
      description: '24/7 support because questions don\'t follow schedules',
      icon: '💬',
    },
    {
      title: 'Results Driven',
      description: 'We measure success by your success',
      icon: '🎯',
    },
  ];

  return (
    <>
      <SEO 
        title="About Us" 
        description="Discover BookMyYogaTTC - Your trusted partner in finding the perfect yoga teacher training program."
      />
      
      {/* Soft, readable background */}
      <div className="pt-20 min-h-screen bg-fog">
        
        {/* Hero Section */}
        <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden bg-white border-b border-midnight/5" style={{ 
          background: 'linear-gradient(to bottom, #FFE8DC 0%, #FFF5F0 50%, #FFE8DC 100%)'
        }}>
          {/* Soft background overlay for depth */}
          <div className="absolute inset-0" style={{
            background: 'radial-gradient(ellipse at 50% 50%, rgba(255, 192, 148, 0.3) 0%, transparent 70%)'
          }} />
          
          {/* Very subtle background elements */}
          <div className="absolute inset-0 overflow-hidden opacity-10">
            <motion.div
              animate={{
                scale: [1, 1.05, 1],
                opacity: [0.03, 0.05, 0.03],
              }}
              transition={{
                duration: 25,
                repeat: Infinity,
                ease: "linear"
              }}
              className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl"
              style={{ background: '#FFD4B8' }}
            />
            <motion.div
              animate={{
                scale: [1.05, 1, 1.05],
                opacity: [0.03, 0.05, 0.03],
              }}
              transition={{
                duration: 20,
                repeat: Infinity,
                ease: "linear"
              }}
              className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full blur-3xl"
              style={{ background: '#FFA870' }}
            />
          </div>

          <div className="container-custom relative z-10 py-20 px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-4xl mx-auto"
            >
              {/* Logo Badge */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                className="inline-flex items-center gap-3 px-6 py-3 rounded-full shadow-sm mb-10 border border-accent/20"
                style={{ background: 'rgba(255, 254, 247, 0.7)', backdropFilter: 'blur(8px)' }}
              >
                <span className="text-5">🧘‍♀️</span>
                <span className="font-semibold text-primary-dark text-4">
                  BookMyYogaTTC
                </span>
              </motion.div>

              {/* Main Heading - High contrast */}
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-5 font-semibold mb-6 leading-tight"
                style={{ color: '#2D2A26', fontWeight: 700 }}
              >
                Find Your Yoga Path
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-4 md:text-5 mb-10 font-medium leading-relaxed max-w-2xl mx-auto"
                style={{ color: '#4A453C', fontWeight: 500 }}
              >
                We help you discover the perfect yoga teacher training that matches your vibe ✨
              </motion.p>

              {/* CTA Buttons - Softer, more readable */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="flex flex-wrap gap-4 justify-center"
              >
                <a
                  href="/courses"
                  className="px-8 py-4 bg-primary text-white font-semibold rounded-full shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-300 hover:bg-primary-light text-3"
                >
                  Explore Programs 🚀
                </a>
                <a
                  href="/contact"
                  className="px-8 py-4 bg-white font-semibold rounded-full shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-300 border-2 border-primary/30 text-primary-dark text-3"
                >
                  Chat With Us 💬
                </a>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Stats Section - Clean white cards */}
        <section className="py-16 px-4" style={{ background: '#FFF5F0' }}>
          <div className="container-custom">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="rounded-2xl p-6 text-center shadow-sm hover:shadow-md transition-shadow duration-300 border border-primary/20"
                  style={{ background: '#F5EDD3' }}
                >
                  <div className="text-5 mb-3">{stat.emoji}</div>
                  <div className="text-5 font-semibold mb-2" style={{ color: '#2D2A26', fontWeight: 700 }}>
                    {stat.number}
                  </div>
                  <div className="font-semibold text-2" style={{ color: '#4A453C', fontWeight: 600 }}>{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* What We're About - Clean section */}
        <section className="py-20 px-4" style={{ background: '#FFE8DC' }}>
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <span className="inline-block px-5 py-2 bg-primary text-white font-semibold rounded-full mb-6 text-2">
                What We're About
              </span>
              <h2 className="text-5 md:text-5 font-semibold mb-5 leading-tight" style={{ color: '#2D2A26', fontWeight: 700 }}>
                We're Here For You 💜
              </h2>
              <p className="text-4 leading-relaxed max-w-2xl mx-auto" style={{ color: '#4A453C', fontWeight: 500 }}>
                No cap, we're all about making your yoga teacher training journey smooth, authentic, and totally yours.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-6">
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="rounded-2xl p-8 shadow-sm hover:shadow-md transition-all duration-300 border border-primary/20"
                  style={{ background: '#F5EDD3' }}
                >
                  <div className="text-5 mb-4">{value.icon}</div>
                  <h3 className="text-5 font-semibold mb-3" style={{ color: '#2D2A26', fontWeight: 700 }}>
                    {value.title}
                  </h3>
                  <p className="leading-relaxed" style={{ color: '#4A453C', fontWeight: 500 }}>
                    {value.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us - Clean section */}
        <section className="py-20 px-4" style={{ background: '#F0E8D8' }}>
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <span className="inline-block px-5 py-2 bg-accent font-semibold rounded-full mb-6 text-2"
                style={{ color: '#3C3530' }}
              >
                Why Us?
              </span>
              <h2 className="text-5 md:text-5 font-semibold mb-5 leading-tight" style={{ color: '#2D2A26', fontWeight: 700 }}>
                Different Hits Different 🔥
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {reasons.map((reason, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow duration-300 text-center border border-primary/20"
                  style={{ background: '#F5EDD3' }}
                >
                  <div className="text-5 mb-4">{reason.icon}</div>
                  <h3 className="text-4 font-semibold mb-3" style={{ color: '#2D2A26', fontWeight: 700 }}>
                    {reason.title}
                  </h3>
                  <p className="text-2 leading-relaxed" style={{ color: '#4A453C', fontWeight: 500 }}>
                    {reason.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Mission Statement - Softer gradient */}
        <section className="py-20 px-4" style={{ background: '#FFE8DC' }}>
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative rounded-2xl p-12 md:p-16 shadow-sm overflow-hidden border border-primary/20"
              style={{ background: '#F5EDD3' }}
            >
              <div className="relative z-10 text-center">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                >
                  <h2 className="text-5 md:text-5 font-semibold mb-6" style={{ color: '#2D2A26', fontWeight: 700 }}>
                    Our Mission? Simple.
                  </h2>
                  <p className="text-5 md:text-5 font-medium mb-8 leading-relaxed max-w-3xl mx-auto" style={{ color: '#4A453C', fontWeight: 500 }}>
                    Connect passionate people with authentic yoga teacher trainings that'll change their lives. Period. ✨
                  </p>
                  <div className="inline-flex items-center gap-3 bg-accent-light/50 rounded-full px-6 py-3 text-3 font-semibold"
                    style={{ color: '#2D2A26', fontWeight: 600 }}
                  >
                    <span>Let's make it happen</span>
                    <span className="text-5">🚀</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Final CTA - Clean and readable */}
        <section className="py-20 px-4" style={{ background: '#F0E8D8' }}>
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="rounded-2xl p-12 md:p-16 shadow-sm text-center border border-primary/20"
              style={{ background: '#F5EDD3' }}
            >
              <h2 className="text-5 md:text-5 font-semibold mb-5" style={{ color: '#2D2A26', fontWeight: 700 }}>
                Ready to Start? 🌟
              </h2>
              <p className="text-4 mb-8 max-w-xl mx-auto leading-relaxed" style={{ color: '#4A453C', fontWeight: 500 }}>
                Let's find the perfect yoga teacher training for you. Your journey starts here.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <a
                  href="/courses"
                  className="px-8 py-4 bg-primary text-white font-semibold rounded-full shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-300 text-3 hover:bg-primary-light"
                >
                  Browse Programs 🎯
                </a>
                <a
                  href="/contact"
                  className="px-8 py-4 bg-accent font-semibold rounded-full shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-300 text-3"
                  style={{ color: '#3C3530' }}
                >
                  Get Started 💫
                </a>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default About;
